/**
 * MarketingSocialPage — Instagram/Facebook-systeem van Kleda: wachtrij met
 * goedkeuring (proefweken), gepubliceerde posts met stats, kandidaten van
 * morgen, het weekplan met leerpunten en de leer-tabellen. Uitvoering zit in
 * GitHub Actions; dit is het toezichtscherm (plannen 2026-09-01 en 2026-09-03).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PIJLERS, useSocialStore, type SocialActPayload, type SocialActie, type SocialPost, type SocialStatus, type Groep, type SocialPijler, type SocialWeekplan, type SocialCaptionBewerking } from '../../stores/social-store';
import { useUserRole } from '../../hooks/useUserRole';
import { MarketingNav, MarketingPageHeader, SectionCard, StatTile, formatDateTime } from './shared';

type Tab = 'wachtrij' | 'gepubliceerd' | 'geskipt' | 'mislukt' | 'kandidaten' | 'weekplan' | 'bewerkingen';

const TAB_STATUS: Record<Exclude<Tab, 'kandidaten' | 'weekplan' | 'bewerkingen'>, SocialStatus> = {
  wachtrij: 'gerenderd',
  gepubliceerd: 'gepubliceerd',
  geskipt: 'geskipt',
  mislukt: 'mislukt',
};

const STATUS_KLEUR: Record<string, string> = {
  kandidaat: 'bg-slate-100 text-slate-600',
  afgekeurd: 'bg-red-50 text-red-700',
  gerenderd: 'bg-amber-50 text-amber-700',
  goedgekeurd: 'bg-blue-50 text-blue-700',
  gepland: 'bg-indigo-50 text-indigo-700',
  gepubliceerd: 'bg-green-50 text-green-700',
  geskipt: 'bg-gray-100 text-gray-600',
  mislukt: 'bg-red-50 text-red-700',
};

const PIJLER_LABEL: Record<SocialPijler, string> = {
  'voor-na': 'voor/na',
  'mooiste-kleed': 'mooiste kleed',
  'achter-de-schermen': 'achter de schermen',
  'klant-aan-het-woord': 'klant aan het woord',
  tips: 'tips',
  ugc: 'ugc',
};

const PIJLER_KLEUR: Record<SocialPijler, string> = {
  'voor-na': 'bg-teal-50 text-teal-700',
  'mooiste-kleed': 'bg-purple-50 text-purple-700',
  'achter-de-schermen': 'bg-orange-50 text-orange-700',
  'klant-aan-het-woord': 'bg-pink-50 text-pink-700',
  tips: 'bg-sky-50 text-sky-700',
  ugc: 'bg-lime-50 text-lime-700',
};

const WEEKDAG = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

function Badge({ tekst, kleur }: { tekst: string; kleur?: string }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${kleur ?? 'bg-slate-100 text-slate-600'}`}>{tekst}</span>;
}

function LeerTabel({ titel, groepen }: { titel: string; groepen: Record<string, Groep> }) {
  const rijen = Object.entries(groepen).sort((a, b) => b[1].reach - a[1].reach);
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 mb-1">{titel}</p>
      {rijen.length === 0 ? (
        <p className="text-xs text-gray-400">Nog geen data</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400">
              <th className="text-left font-normal">groep</th>
              <th className="text-right font-normal">n</th>
              <th className="text-right font-normal">reach</th>
              <th className="text-right font-normal">saves</th>
              <th className="text-right font-normal">shares</th>
            </tr>
          </thead>
          <tbody>
            {rijen.map(([k, g]) => (
              <tr key={k} className="border-t border-gray-100">
                <td className="py-1 text-gray-700">{k}</td>
                <td className="py-1 text-right">{g.n}</td>
                <td className="py-1 text-right">{g.reach.toLocaleString('nl-NL')}</td>
                <td className="py-1 text-right">{g.saves}</td>
                <td className="py-1 text-right">{g.shares}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Lightbox: beeld op ware grootte, pijltjes bij een carrousel, Esc sluit.
 * De thumbnail is 135px breed; een review- of tips-kaart is daarin onleesbaar,
 * dus beoordelen kan alleen als je hem groot kunt bekijken.
 */
function Lightbox({ urls, start, alt, onClose }: { urls: string[]; start: number; alt: string; onClose: () => void }) {
  const [i, setI] = useState(start);
  const vorige = () => setI((x) => (x - 1 + urls.length) % urls.length);
  const volgende = () => setI((x) => (x + 1) % urls.length);

  useEffect(() => {
    function toets(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') vorige();
      if (e.key === 'ArrowRight') volgende();
    }
    window.addEventListener('keydown', toets);
    return () => window.removeEventListener('keydown', toets);
  }, [onClose, urls.length]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={onClose} role="dialog" aria-modal="true">
      <div className="relative max-h-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
        <img src={urls[i]} alt={alt} className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl" />
        <div className="flex items-center gap-4 text-white text-sm">
          {urls.length > 1 && (
            <>
              <button onClick={vorige} className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20">vorige</button>
              <span>{i + 1} / {urls.length}</span>
              <button onClick={volgende} className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20">volgende</button>
            </>
          )}
          <button onClick={onClose} className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20">sluiten (Esc)</button>
        </div>
      </div>
    </div>
  );
}

/** Media-kolom per format: story/foto/carrousel = beeld (klik = lightbox), anders video. */
function PostMedia({ post }: { post: SocialPost }) {
  const [lightbox, setLightbox] = useState<number | null>(null);
  const kaarten = (post.kaarten ?? []).filter((k) => k.url);
  if (!post.render_url) {
    return <div className="w-[135px] h-[169px] rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-400">geen render</div>;
  }
  const isBeeld = post.format === 'story' || post.format === 'foto' || post.format === 'carousel';
  if (!isBeeld) {
    return <video src={post.render_url} poster={post.cover_url ?? undefined} controls preload="metadata" playsInline className="w-[135px] h-[240px] object-cover rounded-lg bg-black" />;
  }
  const urls = post.format === 'carousel' && kaarten.length > 0 ? kaarten.map((k) => k.url as string) : [post.render_url];
  const hoogte = post.format === 'story' ? 'h-[240px]' : 'h-[169px]';
  return (
    <>
      <button type="button" onClick={() => setLightbox(0)} className="relative block group" title="Klik om groot te bekijken">
        <img src={post.render_url} alt={post.alt_text ?? ''} className={`w-[135px] ${hoogte} object-cover rounded-lg bg-gray-100 group-hover:opacity-90`} />
        {urls.length > 1 && <span className="absolute top-1 right-1 text-[10px] bg-black/60 text-white rounded-full px-1.5">1/{urls.length}</span>}
        <span className="absolute bottom-1 right-1 text-[10px] bg-black/60 text-white rounded px-1 opacity-0 group-hover:opacity-100">vergroot</span>
      </button>
      {post.format === 'carousel' && kaarten.length > 1 && (
        <div className="flex gap-1 mt-1 overflow-x-auto max-w-[135px]">
          {kaarten.map((k, i) => (
            <button key={k.url ?? i} type="button" onClick={() => setLightbox(i)}>
              <img src={k.url} alt={k.alt ?? ''} className="w-[40px] h-[50px] object-cover rounded shrink-0 bg-gray-100 hover:opacity-80" />
            </button>
          ))}
        </div>
      )}
      {lightbox !== null && <Lightbox urls={urls} start={lightbox} alt={post.alt_text ?? ''} onClose={() => setLightbox(null)} />}
    </>
  );
}

function PostRij({ post, stats, onAct, zusters = [] }: { post: SocialPost; stats?: { reach: number | null; saves: number | null; shares: number | null; views: number | null } | null; onAct: (a: SocialActie, payload?: SocialActPayload) => Promise<{ caption?: string; hashtags?: string[]; melding?: string } | void>; zusters?: string[] }) {
  const [caption, setCaption] = useState(post.caption ?? '');
  const [hashtags, setHashtags] = useState((post.hashtags ?? []).join(' '));
  const [reden, setReden] = useState('');
  const [skipOpen, setSkipOpen] = useState(false);
  const [herschrijfOpen, setHerschrijfOpen] = useState(false);
  const [aanwijzing, setAanwijzing] = useState('');
  const [melding, setMelding] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const gewijzigd = caption !== (post.caption ?? '') || hashtags !== (post.hashtags ?? []).join(' ');
  const d = post.selectie_detail ?? {};
  const kaart = post.kaarten?.[0];

  async function doe(a: SocialActie, payload?: SocialActPayload) {
    setBusy(a);
    setFout(null);
    setMelding(null);
    try {
      const uit = await onAct(a, payload);
      if (a === 'rewrite' && uit?.caption) {
        setCaption(uit.caption);
        if (uit.hashtags) setHashtags(uit.hashtags.join(' '));
        setHerschrijfOpen(false);
        setAanwijzing('');
        setMelding('Herschreven; lees na en keur goed.');
      } else if (uit?.melding) {
        setMelding(uit.melding);
      }
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Mislukt');
    } finally {
      setBusy(null);
    }
  }

  async function opslaanCaption() {
    await doe('caption', { caption, hashtags: hashtags.split(/\s+/).map((h) => h.replace(/^#/, '')).filter(Boolean) });
  }

  async function goedkeuren(direct = false) {
    if (gewijzigd) await opslaanCaption();
    await doe(direct ? 'publish_now' : 'approve');
  }

  const isWachtrij = post.status === 'gerenderd';

  return (
    <div className="flex gap-4 py-4 border-b border-gray-100 last:border-0">
      <div className="shrink-0 w-[135px]">
        <PostMedia post={post} />
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tekst={post.status} kleur={STATUS_KLEUR[post.status]} />
          <Badge tekst={PIJLER_LABEL[post.pijler] ?? post.pijler} kleur={PIJLER_KLEUR[post.pijler]} />
          <Badge tekst={`${post.platform} / ${post.format}`} />
          {zusters.map((z) => <Badge key={z} tekst={`+ ${z}`} kleur="bg-blue-50 text-blue-700" />)}
          {post.hook_type && <Badge tekst={`hook: ${post.hook_type}`} kleur="bg-gray-100 text-gray-600" />}
          {post.experiment && <Badge tekst={`experiment: ${post.experiment}`} kleur="bg-amber-50 text-amber-700" />}
          {post.selectie_score != null && <Badge tekst={`score ${post.selectie_score}`} kleur="bg-indigo-50 text-indigo-700" />}
          {post.selectie_bron && post.selectie_bron !== 'brein' && <Badge tekst={post.selectie_bron} kleur="bg-yellow-50 text-yellow-700" />}
          {post.wi_sf_id && !post.wi_sf_id.startsWith('compilatie:') && (
            <a href={`/items/${post.wi_sf_id}`} className="text-xs text-indigo-600 hover:underline">
              {post.kleed_soort ?? 'kleed'} {post.kleed_afmeting ?? ''}
            </a>
          )}
          <span className="text-xs text-gray-400">{formatDateTime(post.created_at)}</span>
        </div>
        {post.pijler === 'klant-aan-het-woord' && kaart && (
          <p className="text-xs text-gray-500">
            “{kaart.tekst}” · {kaart.voornaam} · {kaart.bron_label} {kaart.score_label}
          </p>
        )}
        {(d.reden || d.waarom) && <p className="text-xs text-gray-500">{d.kleed_omschrijving ? `${d.kleed_omschrijving} ` : ''}{d.reden ?? d.waarom}</p>}
        {post.fout && <p className="text-xs text-red-600">{post.fout}</p>}
        {post.beoordeel_reden && <p className="text-xs text-gray-500">Reden: {post.beoordeel_reden} ({post.beoordeeld_door})</p>}

        {post.format !== 'story' && (
          <>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={6} disabled={!isWachtrij && post.status !== 'goedgekeurd'} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 disabled:bg-gray-50" />
            <input value={hashtags} onChange={(e) => setHashtags(e.target.value)} disabled={!isWachtrij && post.status !== 'goedgekeurd'} className="w-full text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 disabled:bg-gray-50" placeholder="hashtags gescheiden door spaties" />
            <p className="text-[11px] text-gray-400">
              {caption.length} tekens{post.caption_bewerkt_door ? ` · bewerkt door ${post.caption_bewerkt_door}` : ''}
              {post.caption_origineel && post.caption_origineel !== caption && (isWachtrij || post.status === 'goedgekeurd') && (
                <>
                  {' · '}
                  <button type="button" onClick={() => { setCaption(post.caption_origineel ?? ''); setHashtags((post.hashtags_origineel ?? post.hashtags ?? []).join(' ')); }} className="text-indigo-600 hover:underline" title={post.caption_origineel}>
                    AI-versie terugzetten
                  </button>
                </>
              )}
            </p>
          </>
        )}

        {post.status === 'gepubliceerd' && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
            {post.permalink && (
              <a href={post.permalink} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
                Bekijk post
              </a>
            )}
            <span>{formatDateTime(post.gepubliceerd_op)}</span>
            <Badge tekst={post.actor_bron === 'brein' ? 'autonoom' : 'goedgekeurd door mens'} kleur={post.actor_bron === 'brein' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'} />
            {stats ? (
              <span>
                reach {stats.reach ?? '–'} · views {stats.views ?? '–'} · saves {stats.saves ?? '–'} · shares {stats.shares ?? '–'}
              </span>
            ) : (
              <span className="text-gray-400">nog geen stats</span>
            )}
          </div>
        )}

        {fout && <p className="text-xs text-red-600">{fout}</p>}
        {melding && <p className="text-xs text-green-700">{melding}</p>}

        {isWachtrij && herschrijfOpen && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-indigo-50 px-3 py-2">
            <input value={aanwijzing} onChange={(e) => setAanwijzing(e.target.value)} placeholder="Wat moet anders? bijv. korter, minder over schuim, zeg dat dit Joost is" className="text-sm border border-indigo-200 rounded-lg px-3 py-1.5 w-full md:w-96" />
            <button onClick={() => doe('rewrite', { aanwijzing })} disabled={busy !== null || aanwijzing.trim().length < 3} className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white disabled:opacity-40">
              {busy === 'rewrite' ? 'Schrijft…' : 'Herschrijf'}
            </button>
            <button onClick={() => setHerschrijfOpen(false)} className="text-xs text-gray-500">annuleer</button>
            <span className="text-[11px] text-indigo-900 w-full">De aanwijzing wordt bewaard als leersignaal voor de schrijver.</span>
          </div>
        )}

        {isWachtrij && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button onClick={() => goedkeuren(false)} disabled={busy !== null} className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
              {busy === 'approve' ? 'Bezig…' : 'Goedkeuren'}
            </button>
            <button onClick={() => goedkeuren(true)} disabled={busy !== null} className="px-3 py-1.5 text-sm rounded-lg border border-indigo-600 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50" title="Goedkeuren en de Action nu laten publiceren">
              {busy === 'publish_now' ? 'Bezig…' : 'Plaats nu'}
            </button>
            {post.format !== 'story' && (
              <button onClick={() => setHerschrijfOpen((v) => !v)} disabled={busy !== null} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
                Herschrijf
              </button>
            )}
            {gewijzigd && (
              <button onClick={opslaanCaption} disabled={busy !== null} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
                Caption opslaan
              </button>
            )}
            {!skipOpen ? (
              <button onClick={() => setSkipOpen(true)} disabled={busy !== null} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">
                Skippen
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <input value={reden} onChange={(e) => setReden(e.target.value)} placeholder="Waarom niet? (verplicht)" className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 w-64" />
                <button onClick={() => doe('skip', { reden })} disabled={busy !== null || reden.trim().length < 3} className="px-3 py-1.5 text-sm rounded-lg bg-gray-800 text-white disabled:opacity-40">
                  Bevestig skip
                </button>
                <button onClick={() => setSkipOpen(false)} className="text-xs text-gray-500">annuleer</button>
              </div>
            )}
            <button onClick={() => doe('requeue')} disabled={busy !== null} className="ml-auto text-xs text-gray-500 hover:text-gray-700">
              Opnieuw renderen
            </button>
          </div>
        )}
        {(post.status === 'geskipt' || post.status === 'mislukt') && (
          <div className="flex gap-2 pt-1">
            <button onClick={() => doe('approve')} disabled={busy !== null || (!post.caption && post.format !== 'story')} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
              Toch goedkeuren
            </button>
            <button onClick={() => doe('requeue')} disabled={busy !== null} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
              Opnieuw renderen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Eén voor/na-rij uit social_caption_bewerkingen: wat kantoor veranderde, door wie, hoeveel. */
function BewerkingRij({ b }: { b: SocialCaptionBewerking }) {
  const pct = b.gelijkenis == null ? null : Math.round((1 - Number(b.gelijkenis)) * 100);
  const tagsAnders = (b.hashtags_voor ?? []).join(' ') !== (b.hashtags_na ?? []).join(' ');
  return (
    <div className="py-3 border-b border-gray-100 last:border-0 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {b.pijler && <Badge tekst={PIJLER_LABEL[b.pijler] ?? b.pijler} kleur={PIJLER_KLEUR[b.pijler]} />}
        {b.format && <Badge tekst={b.format} />}
        {b.hook_type && <Badge tekst={`hook: ${b.hook_type}`} kleur="bg-gray-100 text-gray-600" />}
        {pct != null && <Badge tekst={`${pct}% veranderd`} kleur={pct >= 50 ? 'bg-red-50 text-red-700' : pct >= 15 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'} />}
        <span className="text-gray-500">{b.bewerkt_door}</span>
        <span className="text-gray-400">{formatDateTime(b.created_at)}</span>
        {b.status_bij_bewerking && <span className="text-gray-400 ml-auto">bij status {b.status_bij_bewerking}</span>}
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg bg-red-50/50 border border-red-100 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-red-400 mb-1">AI schreef</p>
          <p className="whitespace-pre-line text-gray-700">{b.caption_voor ?? <span className="text-gray-400">leeg</span>}</p>
          {tagsAnders && <p className="text-xs text-gray-400 mt-1">#{(b.hashtags_voor ?? []).join(' #')}</p>}
        </div>
        <div className="rounded-lg bg-green-50/50 border border-green-100 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-green-600 mb-1">kantoor maakte ervan</p>
          <p className="whitespace-pre-line text-gray-900">{b.caption_na ?? <span className="text-gray-400">leeg</span>}</p>
          {tagsAnders && <p className="text-xs text-gray-500 mt-1">#{(b.hashtags_na ?? []).join(' #')}</p>}
        </div>
      </div>
    </div>
  );
}

function WeekplanKaart({ plan, titel }: { plan: SocialWeekplan; titel: string }) {
  const dagen = Object.keys(plan.plan).sort();
  const correcties = plan.cijfers?.correcties ?? [];
  const bovenDrempel = plan.cijfers?.boven_drempel ?? [];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-gray-800">{titel}: week van {plan.week_start}</p>
        <Badge tekst={plan.stuurt ? 'stuurt' : 'schaduw (startmix stuurt)'} kleur={plan.stuurt ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'} />
        <span className="text-xs text-gray-400">{formatDateTime(plan.created_at)}</span>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {dagen.map((datum, i) => {
          const rijen = plan.plan[datum] ?? [];
          const dag = new Date(datum + 'T00:00:00').getDay();
          return (
            <div key={datum} className="border border-gray-100 rounded-lg p-2 min-h-[72px]">
              <p className="text-[11px] text-gray-400 mb-1">{WEEKDAG[dag === 0 ? 6 : dag - 1] ?? WEEKDAG[i]} {datum.slice(8)}</p>
              <div className="flex flex-col gap-1">
                {rijen.map((r, j) => (
                  <Badge key={j} tekst={`${PIJLER_LABEL[r.pijler] ?? r.pijler}${r.format !== 'reel' ? ` (${r.format})` : ''}${r.n > 1 ? ` x${r.n}` : ''}${r.hook_type ? ` · ${r.hook_type}` : ''}`} kleur={PIJLER_KLEUR[r.pijler]} />
                ))}
                {rijen.length === 0 && <span className="text-[11px] text-gray-300">–</span>}
              </div>
            </div>
          );
        })}
      </div>
      {plan.experiment && (
        <p className="text-xs text-gray-700">
          <span className="font-medium">Experiment:</span> hook “{plan.experiment.hook_type}” op {PIJLER_LABEL[plan.experiment.pijler] ?? plan.experiment.pijler}. {plan.experiment.hypothese} <span className="text-gray-500">Meetpunt: {plan.experiment.meetpunt}</span>
        </p>
      )}
      {plan.onderbouwing && <p className="text-xs text-gray-600 whitespace-pre-line">{plan.onderbouwing}</p>}
      <p className="text-[11px] text-gray-400">
        Boven de drempel (10 posts/60d): {bovenDrempel.length ? bovenDrempel.join(', ') : 'geen'}
        {correcties.length > 0 ? ` · vangrail-correcties: ${correcties.join('; ')}` : ''}
      </p>
    </div>
  );
}

export function MarketingSocialPage() {
  const { posts, postStats, stats, kandidaten, weekplan, bewerkingen, pijlerFilter, isLoading, error, fetchPosts, act, fetchStats, fetchKandidaten, fetchWeekplan, fetchBewerkingen, actLeerpunt } = useSocialStore();
  const { role } = useUserRole();
  const isAdmin = role === 'admin';
  const [tab, setTab] = useState<Tab>('wachtrij');
  const [leerBusy, setLeerBusy] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
    fetchWeekplan();
  }, [fetchStats, fetchWeekplan]);

  useEffect(() => {
    if (tab === 'kandidaten') fetchKandidaten();
    else if (tab === 'weekplan') fetchWeekplan();
    else if (tab === 'bewerkingen') fetchBewerkingen(30);
    else fetchPosts(TAB_STATUS[tab]);
  }, [tab, fetchPosts, fetchKandidaten, fetchWeekplan, fetchBewerkingen]);

  // Eén regel per bron x format: de Facebook-rij deelt render en caption en volgt
  // goedkeuren/skippen automatisch, dus die tonen we als badge, niet als rij.
  // Bron = kleed (wi_sf_id) of bron_ref (review, bibliotheek, kb).
  const bronKey = (p: SocialPost) => `${p.wi_sf_id ?? p.bron_ref ?? p.media_ids.join(',')}|${p.format}`;
  const igKeys = new Set(posts.filter((p) => p.platform === 'instagram').map(bronKey));
  const zichtbaar = posts.filter((p) => p.platform === 'instagram' || !igKeys.has(bronKey(p)));
  const zusterPlatforms = (p: SocialPost) =>
    p.platform === 'instagram' ? posts.filter((q) => q.id !== p.id && bronKey(q) === bronKey(p)).map((q) => q.platform) : [];

  const ig = stats?.account.instagram ?? [];
  const laatste = ig[ig.length - 1];
  const fb = stats?.account.facebook ?? [];
  const laatsteFb = fb[fb.length - 1];
  const weekDatum = laatste ? new Date(Date.parse(laatste.snapshot_date) - 7 * 86400_000).toISOString().slice(0, 10) : null;
  const week = ig.find(s => s.snapshot_date === weekDatum);
  const followersDelta = laatste?.followers != null && week?.followers != null ? laatste.followers - week.followers : null;
  const reach7d = stats
    ? stats.posts.filter((p) => p.gepubliceerd_op && Date.now() - new Date(p.gepubliceerd_op).getTime() < 7 * 86400_000).reduce((s, p) => s + (p.stats?.reach ?? 0), 0)
    : 0;
  const reels = stats?.posts.filter((p) => p.format === 'reel' && p.stats) ?? [];
  const gemSaves = reels.length ? Math.round((reels.reduce((s, p) => s + (p.stats?.saves ?? 0), 0) / reels.length) * 10) / 10 : 0;
  const pijlersInStats = new Set((stats?.posts ?? []).map((p) => p.pijler));

  const tabs: Array<{ key: Tab; label: string; n?: number }> = [
    { key: 'wachtrij', label: 'Wachtrij', n: stats?.wachtrij.gerenderd },
    { key: 'gepubliceerd', label: 'Gepubliceerd' },
    { key: 'geskipt', label: 'Geskipt' },
    { key: 'mislukt', label: 'Mislukt', n: stats?.wachtrij.mislukt },
    { key: 'kandidaten', label: 'Kandidaten morgen' },
    { key: 'weekplan', label: 'Weekplan', n: weekplan?.leerpunten.length },
    { key: 'bewerkingen', label: 'Bewerkingen', n: bewerkingen?.bewerkingen.length },
  ];

  const lijstTab = tab !== 'kandidaten' && tab !== 'weekplan' && tab !== 'bewerkingen';

  return (
    <div className="flex-1 overflow-y-auto">
      <MarketingPageHeader
        title="Social"
        subtitle="Instagram en Facebook voor Kleda: elke ochtend Reels, foto's en carrousels uit zes pijlers. Tekst aanpassen of herschrijven mag altijd; wat je verandert leert de schrijver (tab Bewerkingen). Goedkeuren hier, publiceren om 13:00 en 19:00 of direct met Plaats nu."
        actions={<Link to="/marketing/social/keuren" className="shrink-0 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm text-indigo-700 hover:bg-indigo-100">Keuren op je telefoon</Link>}
      />
      <MarketingNav />

      <div className="p-6 space-y-6">
        {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}

        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
          <StatTile label="Instagram-volgers" value={laatste?.followers?.toLocaleString('nl-NL') ?? '–'} sub={followersDelta != null ? `${followersDelta >= 0 ? '+' : ''}${followersDelta} in 7d` : 'nog geen vergelijkbare snapshot'} />
          <StatTile label="Facebook-volgers" value={laatsteFb?.followers?.toLocaleString('nl-NL') ?? '–'} sub={laatsteFb?.followers != null ? `gemeten ${laatsteFb.snapshot_date}` : 'nog geen geldige meting'} />
          <StatTile label="Reach (7d)" value={reach7d.toLocaleString('nl-NL')} sub="som laatste snapshot per post" />
          <StatTile label="Posts (30d)" value={stats?.posts.length ?? 0} sub={`${stats?.posts.filter((p) => p.format === 'reel').length ?? 0} reels · ${pijlersInStats.size} pijler(s)`} />
          <StatTile label="Gem. saves / Reel" value={gemSaves} />
          <StatTile label="% autonoom (MTD)" value={`${stats?.autonoom.pct ?? 0}%`} sub={`${stats?.autonoom.mtd_brein ?? 0} van ${stats?.autonoom.mtd_gepubliceerd ?? 0} zonder mens`} />
          <StatTile label="Weekplan" value={weekplan?.huidig ? (weekplan.huidig.stuurt ? 'stuurt' : 'schaduw') : '–'} sub={weekplan?.huidig ? `week van ${weekplan.huidig.week_start}` : 'nog geen plan'} />
        </div>

        {stats && (
          <section className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
            <h2 className="font-semibold text-gray-900">Doorkliks uit de klantreis (30 dagen)</h2>
            {stats.klantreis ? (
              <>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500"><th className="py-2">Plek</th><th className="text-right">Instagram</th><th className="text-right">Facebook</th></tr></thead>
                  <tbody>{stats.klantreis.sources.map(s => (
                    <tr key={s.source} className="border-t border-gray-100"><td className="py-2">{s.label}</td><td className="text-right tabular-nums">{s.instagram}</td><td className="text-right tabular-nums">{s.facebook}</td></tr>
                  ))}</tbody>
                </table>
                <p className="text-xs text-gray-500">{stats.klantreis.filtered} herkende bots/linkvoorbeelden apart gehouden. Klikken zijn geen unieke personen of nieuwe volgers; niet alle automatische bezoeken zijn herkenbaar. Telling vanaf livegang van de meetlinks op 11 september 2026.</p>
              </>
            ) : <p className="text-sm text-amber-700">Klikmeting tijdelijk niet beschikbaar. Dit betekent niet dat er nul klikken zijn.</p>}
          </section>
        )}

        <div className="flex items-center gap-1 border-b border-gray-200">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {t.label}
              {t.n ? <span className="ml-1 text-xs bg-gray-100 text-gray-600 rounded-full px-1.5">{t.n}</span> : null}
            </button>
          ))}
        </div>

        {lijstTab && (
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-xs text-gray-400 mr-1">Pijler:</span>
            <button onClick={() => fetchPosts(undefined, null)} className={`px-2 py-0.5 rounded-full text-xs ${pijlerFilter === null ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              alle
            </button>
            {PIJLERS.map((p) => (
              <button key={p} onClick={() => fetchPosts(undefined, p)} className={`px-2 py-0.5 rounded-full text-xs ${pijlerFilter === p ? 'bg-gray-800 text-white' : `${PIJLER_KLEUR[p]} hover:opacity-80`}`}>
                {PIJLER_LABEL[p]}
              </button>
            ))}
          </div>
        )}

        {lijstTab && (
          <SectionCard>
            {isLoading && posts.length === 0 && <p className="text-sm text-gray-400">Laden…</p>}
            {!isLoading && posts.length === 0 && (
              <p className="text-sm text-gray-400">
                {tab === 'wachtrij' ? 'Niets te beoordelen. De volgende renders komen morgenochtend 08:30.' : 'Geen posts.'}
              </p>
            )}
            {zichtbaar.length > 0 && zichtbaar.length < posts.length && (
              <p className="text-xs text-gray-400 pb-2">{posts.length - zichtbaar.length} Facebook-rij(en) samengevouwen: goedkeuren geldt voor beide platforms.</p>
            )}
            {zichtbaar.map((p) => (
              <PostRij key={p.id} post={p} stats={postStats[p.id]} onAct={(a, payload) => act(p.id, a, payload)} zusters={zusterPlatforms(p)} />
            ))}
          </SectionCard>
        )}

        {tab === 'kandidaten' && (
          <SectionCard title="Wat de selector morgen 08:30 ziet">
            {!kandidaten && <p className="text-sm text-gray-400">Laden…</p>}
            {kandidaten && (
              <div className="space-y-4">
                <p className="text-xs text-gray-500">
                  {kandidaten.sets.length} complete set(s) (voor + reinigen-video + na) van de laatste 24 uur zonder Reel · {kandidaten.gemarkeerd.length} media vandaag gemarkeerd of gefilmd in de field-app
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {kandidaten.sets.map((s) => (
                    <div key={s.wi_sf_id} className="border border-gray-100 rounded-lg p-2">
                      <div className="flex gap-1">
                        {(['voor', 'tijdens', 'na'] as const).map((f) =>
                          s.thumbs[f] ? (
                            f === 'tijdens' ? (
                              <video key={f} src={s.thumbs[f]!} preload="metadata" className="w-1/3 aspect-square object-cover rounded bg-gray-100" />
                            ) : (
                              <img key={f} src={s.thumbs[f]!} alt={f} className="w-1/3 aspect-square object-cover rounded bg-gray-100" />
                            )
                          ) : (
                            <div key={f} className="w-1/3 aspect-square rounded bg-gray-50" />
                          )
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-600">
                        <a href={`/items/${s.wi_sf_id}`} className="text-indigo-600 hover:underline">{s.item_naam ?? s.wi_sf_id}</a>
                        <span>{s.kleed_soort ?? ''} {s.kleed_afmeting ?? ''}</span>
                        {s.gemarkeerd && <Badge tekst="ster" kleur="bg-yellow-50 text-yellow-700" />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
        )}

        {tab === 'weekplan' && (
          <>
            <SectionCard title="Weekplan (maandag 07:30 door de planner geschreven)">
              {!weekplan && <p className="text-sm text-gray-400">Laden…</p>}
              {weekplan && !weekplan.huidig && <p className="text-sm text-gray-400">Nog geen weekplan. De eerste komt maandagochtend, of via de Action met mode=plan.</p>}
              {weekplan?.huidig && <WeekplanKaart plan={weekplan.huidig} titel="Huidig" />}
              {weekplan?.vorig && (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <WeekplanKaart plan={weekplan.vorig} titel="Vorige week" />
                </div>
              )}
            </SectionCard>
            <SectionCard title="Leerpunten ter beoordeling">
              {weekplan && weekplan.leerpunten.length === 0 && <p className="text-sm text-gray-400">Geen open leerpunten.</p>}
              {weekplan?.leerpunten.map((l) => (
                <div key={l.id} className="flex gap-3 py-3 border-b border-gray-100 last:border-0">
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tekst={l.soort === 'caption' ? 'caption' : 'planning'} kleur={l.soort === 'caption' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-600'} />
                      {l.pijler && <Badge tekst={PIJLER_LABEL[l.pijler] ?? l.pijler} kleur={PIJLER_KLEUR[l.pijler]} />}
                      <Badge tekst={`zekerheid ${l.zekerheid}`} kleur={l.zekerheid === 'hoog' ? 'bg-green-50 text-green-700' : l.zekerheid === 'middel' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'} />
                      <span className="text-xs text-gray-400">week van {l.week_start}</span>
                    </div>
                    <p className="text-sm text-gray-800">{l.leerpunt}</p>
                    {l.bewijs?.tekst && <p className="text-xs text-gray-500">Bewijs: {l.bewijs.tekst}</p>}
                  </div>
                  {isAdmin && <div className="flex items-start gap-2 shrink-0">
                    <button
                      onClick={async () => { setLeerBusy(l.id); try { await actLeerpunt(l.id, 'akkoord'); } finally { setLeerBusy(null); } }}
                      disabled={leerBusy !== null}
                      className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Akkoord
                    </button>
                    <button
                      onClick={async () => { setLeerBusy(l.id); try { await actLeerpunt(l.id, 'afgewezen'); } finally { setLeerBusy(null); } }}
                      disabled={leerBusy !== null}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Afwijzen
                    </button>
                  </div>}
                </div>
              ))}
              {!isAdmin && weekplan && weekplan.leerpunten.length > 0 && <p className="text-xs text-gray-400 pt-2">Leerpunten goedkeuren doet Willem; een akkoord caption-leerpunt wordt een vaste regel voor de schrijver.</p>}
            </SectionCard>
          </>
        )}

        {tab === 'bewerkingen' && (
          <>
            <SectionCard title="Leert de schrijver? (laatste 30 dagen)">
              {!bewerkingen && <p className="text-sm text-gray-400">Laden…</p>}
              {bewerkingen && bewerkingen.per_pijler.length === 0 && <p className="text-sm text-gray-400">Nog niets beoordeeld in deze periode.</p>}
              {bewerkingen && bewerkingen.per_pijler.length > 0 && (
                <table className="w-full text-xs">
                  <thead><tr className="text-gray-400"><th className="text-left font-normal">pijler</th><th className="text-right font-normal">beoordeeld</th><th className="text-right font-normal">tekst aangepast</th><th className="text-right font-normal">aandeel</th><th className="text-right font-normal">gem. veranderd</th></tr></thead>
                  <tbody>{bewerkingen.per_pijler.map((r) => (
                    <tr key={r.pijler} className="border-t border-gray-100">
                      <td className="py-1 text-gray-700">{PIJLER_LABEL[r.pijler as SocialPijler] ?? r.pijler}</td>
                      <td className="py-1 text-right">{r.beoordeeld}</td>
                      <td className="py-1 text-right">{r.bewerkt}</td>
                      <td className="py-1 text-right">{r.bewerkt_rate == null ? '–' : `${Math.round(r.bewerkt_rate * 100)}%`}</td>
                      <td className="py-1 text-right">{r.gem_gelijkenis == null ? '–' : `${Math.round((1 - r.gem_gelijkenis) * 100)}%`}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
              <p className="text-[11px] text-gray-400 mt-2">Het aandeel aangepaste teksten hoort per pijler te dalen: de schrijver krijgt de laatste bewerkingen als voorbeeld mee en akkoord caption-leerpunten als vaste regel. Elke maandag worden de bewerkingen van de week samengevat tot nieuwe leerpunten (tab Weekplan).</p>
            </SectionCard>
            {bewerkingen && bewerkingen.leerpunten.length > 0 && (
              <SectionCard title="Vaste regels die de schrijver nu volgt (akkoord caption-leerpunten)">
                {bewerkingen.leerpunten.map((l) => (
                  <div key={l.id} className="flex flex-wrap items-center gap-2 py-2 border-b border-gray-100 last:border-0 text-sm">
                    {l.pijler ? <Badge tekst={PIJLER_LABEL[l.pijler] ?? l.pijler} kleur={PIJLER_KLEUR[l.pijler]} /> : <Badge tekst="alle pijlers" />}
                    <span className="text-gray-800">{l.leerpunt}</span>
                  </div>
                ))}
              </SectionCard>
            )}
            <SectionCard title="Wat kantoor veranderde">
              {bewerkingen && bewerkingen.bewerkingen.length === 0 && <p className="text-sm text-gray-400">Nog geen bewerkingen in de laatste 30 dagen. Pas een tekst aan in de wachtrij en hij verschijnt hier.</p>}
              {bewerkingen?.bewerkingen.map((b) => <BewerkingRij key={b.id} b={b} />)}
            </SectionCard>
          </>
        )}

        {stats && (
          <SectionCard title="Wat werkt (gepubliceerd, laatste 30 dagen)">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <LeerTabel titel="Per pijler" groepen={stats.leer.perPijler} />
              <LeerTabel titel="Per hook-type" groepen={stats.leer.perHookType} />
              <LeerTabel titel="Per format" groepen={stats.leer.perFormat} />
              <LeerTabel titel="Per kleedsoort" groepen={stats.leer.perKleedSoort} />
              <LeerTabel titel="Per publicatie-uur" groepen={stats.leer.perUur} />
              <LeerTabel titel="Per selectiebron" groepen={stats.leer.perSelectieBron} />
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
}
